import { useApolloClient } from "@apollo/client";
import { MenuItemWithIcon } from "@msys/ui";
import { BugReport as BugReportIcon } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";
import React, { useCallback, useState } from "react";
import { useGlobalLoadingCounter } from "../../global-loading-state/useGlobalLoadingCounter.js";
import { useSapS4HanaGetQuoteAsSalesQuotationLazyQuery } from "./S4HanaGetQuoteAsSalesQuotationMenuButton.generated.js";

type Intent = "GET" | "CREATE" | "UPDATE";

interface Props {
  docId: string;
  projectId: string;
  intent: Intent;
  title: string;
}

export const S4HanaGetQuoteAsSalesQuotationMenuButton = React.forwardRef<
  HTMLLIElement,
  Props
>((props, ref) => {
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");

  const { enqueueSnackbar } = useSnackbar();
  const getQuoteAsSalesQuotation = useGetQuoteAsSalesQuotation();

  const { increment, decrement } = useGlobalLoadingCounter();

  return (
    <MenuItemWithIcon
      ref={ref}
      icon={<BugReportIcon />}
      onClick={async () => {
        try {
          increment();
          setStatus("loading");
          await getQuoteAsSalesQuotation(
            props.projectId,
            props.docId,
            props.intent
          );
          setStatus("idle");
        } catch (e) {
          if (e instanceof Error)
            enqueueSnackbar(e.message, { variant: "error" });
          Sentry.captureException(e);
          setStatus("error");
        } finally {
          decrement();
        }
      }}
      disabled={status === "loading"}
    >
      {props.title}
    </MenuItemWithIcon>
  );
});

const useGetQuoteAsSalesQuotation = () => {
  const client = useApolloClient();

  const [sapS4HanaGetQuoteAsSalesQuotation] =
    useSapS4HanaGetQuoteAsSalesQuotationLazyQuery({ client });

  return useCallback(
    async (projectId: string, docId: string, intent: Intent) => {
      const result = await sapS4HanaGetQuoteAsSalesQuotation({
        variables: {
          projectId,
          docId,
          intent,
        },
      });

      if (result.data) {
        download(
          result.data.sapS4HanaGetQuoteAsSalesQuotation.salesQuotation,
          `quote-as-sales-quotation-${intent}-${docId}.json`
        );
      }
    },
    [sapS4HanaGetQuoteAsSalesQuotation]
  );
};

function download(obj: any, filename: string) {
  const jsonString = JSON.stringify(obj, null, 2); // Pretty formatted
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const elem = document.createElement("a");
  elem.href = url;
  elem.download = filename;
  document.body.appendChild(elem);
  elem.click();
  document.body.removeChild(elem);

  URL.revokeObjectURL(url);
}
